import { NgModule, provideBrowserGlobalErrorListeners } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HttpClient, HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { providePrimeNG } from 'primeng/config';
import Lara from '@primeuix/themes/lara';
import { PublicEventsService, EventTypes } from './lib/angular-auth-oidc-client-facade';
import { filter } from 'rxjs/operators';
import { DebugInterceptor } from './interceptors/debug.interceptor';
import { AppRoutingModule } from './app-routing-module';
import { App } from './app';
import { MainLayout } from './layout/main-layout/main-layout';
import { AlertApiModule } from '@app/api/alert';
import { WeatherApiModule } from '@app/api/weather';
import { environment } from '../environments/environment';
import { AuthConfigModule } from './auth-config.module';
import { ApiModule as CropMonitoringApiModule } from '@6grain/masofa-monolith-api-client/crop-monitoring/api.module';
import { ApiModule as DictApiModule } from '@6grain/masofa-monolith-api-client/dictionaries/api.module';
import { ApiModule as IdentityApiModule } from '@6grain/masofa-monolith-api-client/identity/api.module';
import { ApiModule as CommonApiModule } from '@6grain/masofa-monolith-api-client/common/api.module';
import { ApiModule as SentinelApiModule } from '@6grain/masofa-monolith-api-client/satellite-sentinel/api.module';
import { ApiModule as SatelliteIndicesApiModule } from '@6grain/masofa-monolith-api-client/satellite-indices/api.module';
import { ApiModule as UavApiModule } from '@6grain/masofa-monolith-api-client/uav/api.module';
import { ApiModule as WeatherReportApiModule } from '@6grain/masofa-monolith-api-client/weather-report/api.module';
import { ApiModule as MapApiModule } from '@6grain/masofa-monolith-api-client/map/api.module';
import { ApiModule as WeatherMonolithApiModule } from '@6grain/masofa-monolith-api-client/weather/api.module';
import { ApiModule as AiInferenceApiModule } from '@6grain/masofa-monolith-api-client/ai-inference/api.module';
import { Configuration as CropMonitoringConfiguration } from '@6grain/masofa-monolith-api-client/crop-monitoring/configuration';
import { Configuration as DictConfiguration } from '@6grain/masofa-monolith-api-client/dictionaries/configuration';
import { Configuration as IdentityConfiguration } from '@6grain/masofa-monolith-api-client/identity/configuration';
import { Configuration as CommonConfiguration } from '@6grain/masofa-monolith-api-client/common/configuration';
import { Configuration as SentinelConfiguration } from '@6grain/masofa-monolith-api-client/satellite-sentinel/configuration';
import { Configuration as SatelliteIndicesConfiguration } from '@6grain/masofa-monolith-api-client/satellite-indices/configuration';
import { Configuration as UavConfiguration } from '@6grain/masofa-monolith-api-client/uav/configuration';
import { Configuration as WeatherReportConfiguration } from '@6grain/masofa-monolith-api-client/weather-report/configuration';
import { Configuration as MapConfiguration } from '@6grain/masofa-monolith-api-client/map/configuration';
import { Configuration as WeatherConfiguration } from '@6grain/masofa-monolith-api-client/weather/configuration';
import { Configuration as AiInferenceConfiguration } from '@6grain/masofa-monolith-api-client/ai-inference/configuration';
import { BASE_PATH as CROP_BASE_PATH } from '@6grain/masofa-monolith-api-client/crop-monitoring/variables';
import { BASE_PATH as DICT_BASE_PATH } from '@6grain/masofa-monolith-api-client/dictionaries/variables';
import { BASE_PATH as IDENTITY_BASE_PATH } from '@6grain/masofa-monolith-api-client/identity/variables';
import { BASE_PATH as UAV_BASE_PATH } from '@6grain/masofa-monolith-api-client/uav/variables';
import { BASE_PATH as WEATHER_REPORT_BASE_PATH } from '@6grain/masofa-monolith-api-client/weather-report/variables';
import { BASE_PATH as MAP_BASE_PATH } from '@6grain/masofa-monolith-api-client/map/variables';
import { BASE_PATH as WEATHER_BASE_PATH } from '@6grain/masofa-monolith-api-client/weather/variables';
import { CommonModule, DatePipe } from '@angular/common';
import { Toast } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { CustomMessageService } from '@app/interceptors/custom-message';
import { provideAppLanguageManager } from '@components/select-language/app-language-manager';

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

export function createCropMonitoringApiConfiguration(): CropMonitoringConfiguration {
  const basePath = environment.api;
  return new CropMonitoringConfiguration({
    basePath: basePath,
    credentials: {
      Bearer: () => {
        let token = localStorage.getItem('access_token');

        if (!token) {
          const keys = Object.keys(localStorage);
          for (const key of keys) {
            if (key.includes('authnResult') || key.includes('oidc.user')) {
              try {
                const data = JSON.parse(localStorage.getItem(key) || '{}');
                if (data.access_token) {
                  token = data.access_token;
                  break;
                }
              } catch (e) {}
            }
          }
        }

        if (!token) {
          token = sessionStorage.getItem('access_token');
        }

        return token || '';
      },
    },
    withCredentials: false,
  });
}

export function createDictApiConfiguration(): DictConfiguration {
  const basePath = environment.api;
  return new DictConfiguration({
    basePath: basePath,
    credentials: {
      Bearer: () => {
        let token = localStorage.getItem('access_token');

        if (!token) {
          const keys = Object.keys(localStorage);
          for (const key of keys) {
            if (key.includes('authnResult') || key.includes('oidc.user')) {
              try {
                const data = JSON.parse(localStorage.getItem(key) || '{}');
                if (data.access_token) {
                  token = data.access_token;
                  break;
                }
              } catch (e) {}
            }
          }
        }

        if (!token) {
          token = sessionStorage.getItem('access_token');
        }

        return token || '';
      },
    },
    withCredentials: false,
  });
}

export function createSentinelConfiguration(): SentinelConfiguration {
  const basePath = environment.api;
  return new SentinelConfiguration({
    basePath: basePath,
    credentials: {
      Bearer: () => {
        let token = localStorage.getItem('access_token');

        if (!token) {
          const keys = Object.keys(localStorage);
          for (const key of keys) {
            if (key.includes('authnResult') || key.includes('oidc.user')) {
              try {
                const data = JSON.parse(localStorage.getItem(key) || '{}');
                if (data.access_token) {
                  token = data.access_token;
                  break;
                }
              } catch (e) {}
            }
          }
        }

        if (!token) {
          token = sessionStorage.getItem('access_token');
        }

        return token || '';
      },
    },
    withCredentials: false,
  });
}

export function createSatelliteIndicesConfiguration(): SatelliteIndicesConfiguration {
  const basePath = environment.api;
  return new SatelliteIndicesConfiguration({
    basePath,
    credentials: {
      Bearer: () => {
        let token = localStorage.getItem('access_token');

        if (!token) {
          const keys = Object.keys(localStorage);
          for (const key of keys) {
            if (key.includes('authnResult') || key.includes('oidc.user')) {
              try {
                const data = JSON.parse(localStorage.getItem(key) || '{}');
                if (data.access_token) {
                  token = data.access_token;
                  break;
                }
              } catch (e) {}
            }
          }
        }

        if (!token) {
          token = sessionStorage.getItem('access_token');
        }

        return token || '';
      },
    },
    withCredentials: false,
  });
}

export function createIdentityApiConfiguration(): IdentityConfiguration {
  const basePath = environment.api;
  return new IdentityConfiguration({
    basePath: basePath,
    credentials: {
      Bearer: () => {
        let token = localStorage.getItem('access_token');

        if (!token) {
          const keys = Object.keys(localStorage);
          for (const key of keys) {
            if (key.includes('authnResult') || key.includes('oidc.user')) {
              try {
                const data = JSON.parse(localStorage.getItem(key) || '{}');
                if (data.access_token) {
                  token = data.access_token;
                  break;
                }
              } catch (e) {}
            }
          }
        }

        if (!token) {
          token = sessionStorage.getItem('access_token');
        }

        return token || '';
      },
    },
    withCredentials: false,
  });
}

export function createCommonApiConfiguration(): CommonConfiguration {
  const basePath = environment.api;
  return new CommonConfiguration({
    basePath: basePath,
    credentials: {
      Bearer: () => {
        let token = localStorage.getItem('access_token');

        if (!token) {
          const keys = Object.keys(localStorage);
          for (const key of keys) {
            if (key.includes('authnResult') || key.includes('oidc.user')) {
              try {
                const data = JSON.parse(localStorage.getItem(key) || '{}');
                if (data.access_token) {
                  token = data.access_token;
                  break;
                }
              } catch (e) {}
            }
          }
        }

        if (!token) {
          token = sessionStorage.getItem('access_token');
        }

        return token || '';
      },
    },
    withCredentials: false,
  });
}
export function createUavApiConfiguration(): UavConfiguration {
  const basePath = environment.api;
  return new UavConfiguration({
    basePath: basePath,
    credentials: {
      Bearer: () => {
        let token = localStorage.getItem('access_token');

        if (!token) {
          const keys = Object.keys(localStorage);
          for (const key of keys) {
            if (key.includes('authnResult') || key.includes('oidc.user')) {
              try {
                const data = JSON.parse(localStorage.getItem(key) || '{}');
                if (data.access_token) {
                  token = data.access_token;
                  break;
                }
              } catch (e) {}
            }
          }
        }

        if (!token) {
          token = sessionStorage.getItem('access_token');
        }

        return token || '';
      },
    },
    withCredentials: false,
  });
}

export function createWeatherReportApiConfiguration(): WeatherReportConfiguration {
  const basePath = environment.api;
  return new WeatherReportConfiguration({
    basePath: basePath,
    credentials: {
      Bearer: () => {
        let token = localStorage.getItem('access_token');

        if (!token) {
          const keys = Object.keys(localStorage);
          for (const key of keys) {
            if (key.includes('authnResult') || key.includes('oidc.user')) {
              try {
                const data = JSON.parse(localStorage.getItem(key) || '{}');
                if (data.access_token) {
                  token = data.access_token;
                  break;
                }
              } catch (e) {}
            }
          }
        }

        if (!token) {
          token = sessionStorage.getItem('access_token');
        }

        return token || '';
      },
    },
    withCredentials: false,
  });
}

export function createMapConfiguration(): MapConfiguration {
  const basePath = environment.api;
  return new MapConfiguration({
    basePath: basePath,
    credentials: {
      Bearer: () => {
        let token = localStorage.getItem('access_token');

        if (!token) {
          const keys = Object.keys(localStorage);
          for (const key of keys) {
            if (key.includes('authnResult') || key.includes('oidc.user')) {
              try {
                const data = JSON.parse(localStorage.getItem(key) || '{}');
                if (data.access_token) {
                  token = data.access_token;
                  break;
                }
              } catch (e) {}
            }
          }
        }

        if (!token) {
          token = sessionStorage.getItem('access_token');
        }

        return token || '';
      },
    },
    withCredentials: false,
  });
}

export function createWeatherApiConfiguration(): WeatherConfiguration {
  const basePath = environment.api;
  return new WeatherConfiguration({
    basePath: basePath,
    credentials: {
      Bearer: () => {
        let token = localStorage.getItem('access_token');

        if (!token) {
          const keys = Object.keys(localStorage);
          for (const key of keys) {
            if (key.includes('authnResult') || key.includes('oidc.user')) {
              try {
                const data = JSON.parse(localStorage.getItem(key) || '{}');
                if (data.access_token) {
                  token = data.access_token;
                  break;
                }
              } catch (e) {}
            }
          }
        }

        if (!token) {
          token = sessionStorage.getItem('access_token');
        }

        return token || '';
      },
    },
    withCredentials: false,
  });
}

export function createAiInferenceApiConfiguration(): AiInferenceConfiguration {
  const basePath = environment.api;
  return new AiInferenceConfiguration({
    basePath: basePath,
    credentials: {
      Bearer: () => {
        let token = localStorage.getItem('access_token');

        if (!token) {
          const keys = Object.keys(localStorage);
          for (const key of keys) {
            if (key.includes('authnResult') || key.includes('oidc.user')) {
              try {
                const data = JSON.parse(localStorage.getItem(key) || '{}');
                if (data.access_token) {
                  token = data.access_token;
                  break;
                }
              } catch (e) {}
            }
          }
        }

        if (!token) {
          token = sessionStorage.getItem('access_token');
        }

        return token || '';
      },
    },
    withCredentials: false,
  });
}

@NgModule({
  declarations: [
    App
  ],
  imports: [
    BrowserModule,
    // ThemeModule,
    BrowserAnimationsModule,
    HttpClientModule,
    AuthConfigModule,
    AppRoutingModule,
    MainLayout,
    CommonModule,
    TranslateModule.forRoot({
      defaultLanguage: 'ru-RU',
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    AlertApiModule.forRoot({ rootUrl: environment.gateway }),
    WeatherApiModule.forRoot({ rootUrl: environment.gateway }),
    CropMonitoringApiModule.forRoot(createCropMonitoringApiConfiguration),
    DictApiModule.forRoot(createDictApiConfiguration),
    IdentityApiModule.forRoot(createIdentityApiConfiguration),
    CommonApiModule.forRoot(createCommonApiConfiguration),
    SentinelApiModule.forRoot(createSentinelConfiguration as any),
    SatelliteIndicesApiModule.forRoot(createSatelliteIndicesConfiguration as any),
    UavApiModule.forRoot(createUavApiConfiguration),
    WeatherReportApiModule.forRoot(createWeatherReportApiConfiguration),
    MapApiModule.forRoot(createMapConfiguration),
    WeatherMonolithApiModule.forRoot(createWeatherApiConfiguration),
    AiInferenceApiModule.forRoot(createAiInferenceApiConfiguration),
    Toast,
  ],
  providers: [
    DatePipe,
    {
      provide: MessageService,
      useClass: CustomMessageService,
    },
    provideBrowserGlobalErrorListeners(),
    provideAppLanguageManager(),
    providePrimeNG({
      theme: {
        preset: Lara,
        options: {
          darkModeSelector: '.dark',
        },
      },
    }),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: DebugInterceptor,
      multi: true,
    },
    {
      provide: CROP_BASE_PATH,
      useValue: environment.api,
    },
    {
      provide: DICT_BASE_PATH,
      useValue: environment.api,
    },
    {
      provide: IDENTITY_BASE_PATH,
      useValue: environment.api,
    },
    {
      provide: WEATHER_REPORT_BASE_PATH,
      useValue: environment.api,
    },
    {
      provide: MAP_BASE_PATH,
      useValue: environment.api,
    },
    {
      provide: WEATHER_BASE_PATH,
      useValue: environment.api,
    },
    {
      provide: UAV_BASE_PATH,
      useValue: environment.api,
    },
  ],
  bootstrap: [App],
})
export class AppModule {
  constructor(private readonly eventService: PublicEventsService) {
    this.eventService
      .registerForEvents()
      .pipe(filter((notification) => notification?.type === EventTypes.ConfigLoaded))
      .subscribe(() => {});
  }
}
